<script setup lang="ts">
import { ref, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { $t, usePrevioCore } from '@previo/core'
import { type IErrorState } from './model/ErrorState'
import Segment from './components/Segment.vue'

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
}

const props = defineProps({
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

const model = defineModel<Dayjs | null>()
const emits = defineEmits<Emits>()

const { UUID } = usePrevioCore()

const id = UUID.v7()

const segmentComponent = ref<InstanceType<typeof Segment>>()
const text = ref<string>('HH')
const buffer = ref<string>('')

const isValid = (): boolean => {
  if (!model.value) {
    return false
  }

  return model.value.tz().isValid() && text.value !== 'HH'
}

const validate = (errorState: IErrorState): void => {
  if (isValid()) {
    return
  }

  errorState.set($t('The value {value} is required', { value: 'HH' }))
}

const isValidHour = (hour: number): boolean => {
  return hour >= 0 && hour <= 23
}

const focus = (): void => {
  segmentComponent.value?.focus()
}

const ensureValid = (): void => {
  const raw = buffer.value.trim()

  if (!/^\d+$/.test(raw)) {
    return
  }

  const hour = parseInt(raw, 10)

  // Allow only 0–23 for 24-hour format
  if (hour < 0 || hour > 23) {
    return
  }

  if (!model.value) {
    return
  }

  model.value = model.value.tz().hour(hour)

  emits('next')
}

const onInput = (value: string): void => {
  buffer.value = (buffer.value + value).slice(-2)
  const hour = parseInt(buffer.value, 10)

  if (buffer.value.length === 2) {
    const base =
      model.value ?? (props.defaultTime ? dayjs(props.defaultTime, 'HH:mm') : dayjs().tz())
    const safeHour = isValidHour(hour) ? hour : Math.min(Math.max(hour, 0), 23)

    model.value = base.set('hour', safeHour)

    emits('next')
    buffer.value = ''
  } else {
    text.value = buffer.value
  }
}

const onDelete = (): void => {
  buffer.value = ''
  text.value = 'HH'
}

const onAdd = (): void => {
  model.value = model.value?.tz().add(1, 'hour')
}

const onSubtract = (): void => {
  model.value = model.value?.tz().subtract(1, 'hour')
}

const onNext = (): void => {
  ensureValid()
  emits('next')
}

const onPrev = (): void => {
  ensureValid()
  emits('prev')
}

const onConfirm = (): void => {
  ensureValid()
}

watch(
  model,
  () => {
    if (!model.value) {
      text.value = 'HH'
      return
    }

    text.value = model.value.tz().format('HH')
  },
  { immediate: true },
)

defineExpose({
  focus,
  validate,
})
</script>

<template>
  <segment
    :id="id"
    ref="segmentComponent"
    :text="text"
    @input="onInput"
    @delete="onDelete"
    @next="onNext"
    @prev="onPrev"
    @add="onAdd"
    @subtract="onSubtract"
    @confirm="onConfirm"
  />
</template>
